<script lang="ts">
  import Button from '$components/elements/Button.svelte';
  import DashboardFile from '$components/elements/DashboardFile.svelte';
  import Icon from '$components/elements/Icon.svelte';
  import Modal from '$components/elements/Modal.svelte';
  import ModalImageHeader from '$components/elements/ModalImageHeader.svelte';
  import Grid from '$components/layout/Grid.svelte';
  import Desktop from '$components/utils/Desktop.svelte';
  import { getAllKeyValue as getGlobalValues } from '$lib/pocketbase/admin/global-values';
  import { getAll as getPremiumFiles } from '$lib/pocketbase/admin/premium-files';
  import { pledgeInfo, premiumFiles, showDashboard, showDashboardSettings } from '$lib/stores';
  import { globalValues } from '$lib/stores/global-values';
  import { onMount } from 'svelte';
  import DonationsDetails from './DonationsDetails.svelte';
  import { getPledgeData } from '$lib/client/pocketbase/pledge';
  import { currentUser, pbClient } from '$lib/client/pocketbase';
  import { logout } from '$lib/client/pocketbase/users';

  let showDonations = false;

  function copy(string: string) {
    if (!string) return;
    navigator.clipboard.writeText(string);
  }

  pbClient.authStore.onChange(async () => {
    premiumFiles.set(await getPremiumFiles(pbClient));
    pledgeInfo.set(await getPledgeData());
  });

  onMount(async () => {
    premiumFiles.set(await getPremiumFiles(pbClient));
    pledgeInfo.set(await getPledgeData());
    globalValues.set(await getGlobalValues(pbClient));
  });
</script>

<Modal width="1096px" showModal={$showDashboard} onClose={() => showDashboard.set(false)}>
  <div class="dashboard">
    <Desktop>
      <div class="dashboard__closer">
        <Icon
          icon="cross"
          on:click={() => showDashboard.set(false)}
          cursor="pointer"
          casing="30px"
        />
      </div>
    </Desktop>
    <ModalImageHeader>
      <div
        class="dashboard__settings-button"
        on:click={() => {
          showDashboardSettings.set(true);
          showDashboard.set(false);
        }}
        on:keypress={() => {
          showDashboardSettings.set(true);
          showDashboard.set(false);
        }}
      >
        <Icon icon="settings-2" />
        <Desktop>
          <div class="dashboard__settings-button__text">Nastavení</div>
        </Desktop>
      </div>
      <h1>
        <span>{$currentUser?.name} {$currentUser?.surname}</span><br />Vítejte v Moje Bible21.<br
        />Děkujeme za vaši podporu!
      </h1>
    </ModalImageHeader>
    <Grid
      breakpoints={{
        0: {
          templateColumns: '1fr',
          margin: '37px 16px',
          width: 'calc(100% - 32px)'
        },
        720: {
          templateColumns: '1fr 1fr',
          gap: {
            column: '36px',
            row: '30px'
          },
          margin: '37px 90px',
          width: 'calc(100% - 180px)',
          alignItems: 'start'
        }
      }}
    >
      <div class="dashboard__section">
        <div class="dashboard__section__header">
          <Icon icon="shopping-cart" color="#C99E77" />
          Slevový kód do e-knihkupectví www.biblion.cz
        </div>
        <div class="dashboard__section__content">
          <div class="dashboard__section__content__card">
            <p class="dashboard__section__content__card__text">
              <span>
                {#if $globalValues.coupon !== undefined}
                  {$globalValues.coupon}
                {:else}
                  Chyba
                {/if}
              </span>
            </p>
            <Icon icon="copy" on:click={() => copy($globalValues.coupon ?? '')} cursor="pointer" />
          </div>
          <Button
            icon="shopping-cart"
            border="1.5px solid #1D1D1B"
            color="transparent"
            textColor="#1D1D1B"
            href="https://biblion.cz"
            target="_blank"
            iconSize={24}>Přejít do Biblionu</Button
          >
        </div>
      </div>
      <div class="dashboard__section">
        <div class="dashboard__section__header">
          <Icon icon="credit-card" color="#C99E77" />
          Platební údaje
        </div>
        <div class="dashboard__section__content">
          <div class="dashboard__section__content__card">
            <p class="dashboard__section__content__card__text">
              Číslo účtu:
              <span>
                {#if $globalValues.accountNumber !== undefined}
                  {$globalValues.accountNumber}
                {:else}
                  Chyba
                {/if}
              </span>
            </p>
            <Icon
              icon="copy"
              on:click={() => copy($globalValues.accountNumber ?? '')}
              cursor="pointer"
            />
          </div>
          {#if $pledgeInfo}
            <div class="dashboard__section__content__card">
              <p class="dashboard__section__content__card__text">
                VS: <span>{$pledgeInfo.variableSymbol}</span>
              </p>
              <Icon
                icon="copy"
                on:click={() => copy(`${$pledgeInfo?.variableSymbol}`)}
                cursor="pointer"
              />
            </div>
            <Button
              icon="credit-card"
              border="1.5px solid #1D1D1B"
              color="transparent"
              textColor="#1D1D1B"
              iconSize={24}
              on:click={() => {
                showDonations = true;
                showDashboard.set(false);
              }}
              >Moje příspěvky a potvrzení
            </Button>
          {/if}
        </div>
      </div>
      <div class="dashboard__section dashboard__section--wide">
        <div class="dashboard__section__header">
          <Icon icon="download" color="#C99E77" />
          Vydané svazky Studijní Bible21 ke stažení
        </div>
        <div class="dashboard__section__files">
          {#each $premiumFiles as file}
            <DashboardFile href={file.file}>{file.name}</DashboardFile>
          {/each}
        </div>
      </div>
    </Grid>
    <Button
      on:click={() => {
        logout();
        showDashboard.set(false);
      }}
      >Odhlásit se z mého účtu
    </Button>
  </div>
</Modal>

<DonationsDetails bind:show={showDonations} />

<style lang="scss">
  .dashboard {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: 100%;
    padding-bottom: 60px;
    position: relative;

    h1 {
      font-weight: 700;
      font-size: 28px;
      line-height: 38px;
      text-align: center;

      span {
        color: $color-caramel;
      }
    }

    &__closer {
      position: absolute;
      top: 20px;
      right: 25px;
    }

    &__section {
      width: 100%;

      &__header {
        display: flex;
        align-items: center;
        gap: 9px;
        border-bottom: 1.5px solid $color-medium-gray;
        padding-bottom: 6px;
        color: $color-caramel;
        font-size: 14px;
      }

      &__content {
        display: flex;
        flex-direction: column;
        gap: 22px;
        padding: 16px 0;

        &__card {
          width: 100%;
          border: 1.5px solid $color-medium-gray;
          padding: 27px 32px;
          border-radius: 13px;
          display: flex;
          justify-content: space-between;
          align-items: center;

          &__text {
            font-size: 14px;
            font-weight: 500;

            span {
              font-weight: 700;
            }
          }
        }
      }

      &__files {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        padding: 16px 0;
      }

      &--wide {
        grid-column: span 2;
      }
    }

    &__settings-button {
      position: absolute;
      top: 20px;
      left: 25px;
      display: flex;
      align-items: center;
      gap: 12px;
      cursor: pointer;
      overflow: hidden;
      transition: 0.2s ease all;

      &__text {
        transition: 0.2s ease all;
        opacity: 0;
      }

      &:hover {
        .dashboard {
          &__settings-button {
            &__text {
              opacity: 1;
            }
          }
        }
      }
    }
  }

  @include mobile {
    .dashboard {
      &__settings-button {
        top: 16px;
        left: 16px;

        &__text {
          opacity: 1;
        }
      }

      &__section {
        &__content {
          align-items: center;
        }

        &--wide {
          grid-column: span 1;
        }
      }
    }
  }
</style>
